import { useState } from 'react';
import { agencyRoleLabelEn } from '@/utils/agencyRole';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  useAdminUser,
  useDeleteUser,
  useChangeUserRole,
  useResetUserPassword,
  useImpersonationHistory,
  useUpdateUser,
  useAddAgencyStaff,
  useRemoveAgencyStaff,
  useAdminAgencies,
  useAdminTeams,
} from '@/hooks/useAdmin';
import { AgencyStaffRole, CreateUserRequest } from '@/types/admin';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog, Card, CardHeader, CardBody, Badge } from '@/components/ui';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Combobox } from '@/components/ui/Combobox';
import { Modal } from '@/components/ui/Modal';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { useTimezone } from '@/contexts/TimezoneContext';
import { toast } from '@/utils/toast';
import { formatApiError } from '@/services/api';
import * as Dialog from '@radix-ui/react-dialog';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useDebounce } from '@/hooks/useDebounce';
import { isLastReviewerCancel } from '@/stores/lastReviewerConfirm.store';

/**
 * Admin User Detail Page
 * View and manage individual user details
 */

const ROLES = ['USER', 'ADMIN', 'AGENCY_ADMIN', 'SUPER_ADMIN'] as const;

/**
 * Calculate duration between two dates in a human-readable format
 */
function calculateDuration(startDate: string, endDate: string | null): string {
  if (!endDate) return 'In Progress';

  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  } else if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
  } else {
    return 'Less than 1 minute';
  }
}

export const AdminUserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();
  const userId = parseInt(id || '0', 10);

  const { data: user, isLoading, error } = useAdminUser(userId);
  const deleteUser = useDeleteUser();
  const changeRole = useChangeUserRole();
  const resetPassword = useResetUserPassword();
  const updateUser = useUpdateUser();
  const addAgencyStaff = useAddAgencyStaff(userId);
  const removeAgencyStaff = useRemoveAgencyStaff(userId);

  const {
    data: impersonationHistory,
    isLoading: isLoadingHistory,
    error: historyError
  } = useImpersonationHistory({ targetUserId: userId, limit: 10 });

  const [selectedRole, setSelectedRole] = useState<typeof ROLES[number] | null>(null);
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [isSuperAdminPending, setIsSuperAdminPending] = useState<boolean | null>(null);
  const [superAdminConfirmDialog, setSuperAdminConfirmDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showAddAgencyStaffModal, setShowAddAgencyStaffModal] = useState(false);
  const [removeAgencyConfirm, setRemoveAgencyConfirm] = useState<{ open: boolean; agencyId: number | null; agencyName: string }>({ open: false, agencyId: null, agencyName: '' });

  const handleDelete = async () => {
    if (!user) return;
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!user) return;

    try {
      await deleteUser.mutateAsync(user.id);
      toast.success(`User ${user.email} deleted`);
      setDeleteDialog(false);
      navigate('/admin/users');
    } catch (err) {
      if (isLastReviewerCancel(err)) return;
      toast.error('Failed to delete user');
      setDeleteDialog(false);
    }
  };

  const handleRoleChange = async () => {
    if (!user || !selectedRole) return;

    try {
      await changeRole.mutateAsync({ id: user.id, role: selectedRole });
      toast.success(`Role updated to ${selectedRole}`);
      setIsChangingRole(false);
      setSelectedRole(null);
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  const handleResetPassword = async () => {
    if (!user) return;
    setResetPasswordDialog(true);
  };

  const handleResetPasswordConfirm = async () => {
    if (!user) return;

    try {
      const result = await resetPassword.mutateAsync(user.id);
      toast.success('Password reset email sent. You can also copy the link below.');
      setResetPasswordDialog(false);
      setResetLink(result.resetUrl);
    } catch (err) {
      toast.error('Failed to send password reset email');
      setResetPasswordDialog(false);
    }
  };

  const handleCopyResetLink = async () => {
    if (!resetLink) return;
    try {
      await navigator.clipboard.writeText(resetLink);
      toast.success('Reset link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleSuperAdminToggle = (checked: boolean) => {
    if (checked) {
      // Show confirmation before granting super-admin
      setIsSuperAdminPending(true);
      setSuperAdminConfirmDialog(true);
    } else {
      // Revoking super-admin — no confirmation needed
      setIsSuperAdminPending(false);
      setSuperAdminConfirmDialog(true);
    }
  };

  const handleSuperAdminSaveConfirm = async () => {
    if (!user || isSuperAdminPending === null) return;
    try {
      await updateUser.mutateAsync({ id: user.id, data: { isSuperAdmin: isSuperAdminPending } });
      toast.success(`Super admin ${isSuperAdminPending ? 'granted' : 'revoked'}`);
      setSuperAdminConfirmDialog(false);
      setIsSuperAdminPending(null);
    } catch (err) {
      if (isLastReviewerCancel(err)) return;
      toast.error('Failed to update super admin status');
      setSuperAdminConfirmDialog(false);
      setIsSuperAdminPending(null);
    }
  };

  const handleRemoveAgencyStaffConfirm = async () => {
    if (!removeAgencyConfirm.agencyId) return;
    try {
      await removeAgencyStaff.mutateAsync(removeAgencyConfirm.agencyId);
      toast.success(`Removed from ${removeAgencyConfirm.agencyName}`);
      setRemoveAgencyConfirm({ open: false, agencyId: null, agencyName: '' });
    } catch (err) {
      if (isLastReviewerCancel(err)) return;
      toast.error('Failed to remove portfolio staff');
      setRemoveAgencyConfirm({ open: false, agencyId: null, agencyName: '' });
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <AdminPageHeader title="User Details" backTo="/admin/users" backLabel="Back to Users" />
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          Error loading user: {formatApiError(error).message}
        </div>
      </div>
    );
  }

  if (isLoading || !user) {
    return (
      <div className="p-8 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <AdminPageHeader
        title={user.fullName || 'No name'}
        subtitle={user.email}
        backTo="/admin/users"
        backLabel="Back to Users"
        action={
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowEditProfileModal(true)}
            >
              Edit Profile
            </Button>
            <Button
              variant="secondary"
              onClick={handleResetPassword}
              disabled={resetPassword.isPending}
              loading={resetPassword.isPending}
            >
              Reset Password
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deleteUser.isPending}
              loading={deleteUser.isPending}
            >
              Delete User
            </Button>
          </div>
        }
      />

      {/* User Info Card */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">User Information</h2>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-600">Full Name</label>
              <p className="mt-1 text-gray-900">{user.fullName || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Username</label>
              <p className="mt-1 text-gray-900">{user.username || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Email</label>
              <p className="mt-1 text-gray-900">{user.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Timezone</label>
              <p className="mt-1 text-gray-900">{user.timezone || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Status</label>
              <div className="mt-1">
                <Badge variant={user.isActive ? 'success' : 'danger'}>
                  {user.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Super Admin</label>
              <div className="mt-1">
                <Badge variant={user.isSuperAdmin ? 'primary' : 'neutral'}>
                  {user.isSuperAdmin ? 'Yes' : 'No'}
                </Badge>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Role Management Card */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Role Management</h2>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-600">Current Role</label>
            <p className="mt-1">
              <Badge variant="info" className="px-3 py-1.5 text-sm">
                {user.role}
              </Badge>
            </p>
          </div>

          {!isChangingRole ? (
            <Button variant="secondary" onClick={() => setIsChangingRole(true)}>
              Change Role
            </Button>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">
                  New Role
                </label>
                <Select
                  value={selectedRole || user.role}
                  onValueChange={(value) => setSelectedRole(value as typeof ROLES[number])}
                  options={ROLES.map(role => ({
                    value: role,
                    label: role
                  }))}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleRoleChange}
                  disabled={!selectedRole || selectedRole === user.role || changeRole.isPending}
                  loading={changeRole.isPending}
                >
                  Save Role
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsChangingRole(false);
                    setSelectedRole(null);
                  }}
                  disabled={changeRole.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Super Admin Toggle */}
          <div className="pt-4 border-t border-gray-100">
            <label className="text-sm font-medium text-gray-600 block mb-2">Super Admin</label>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="super-admin-toggle"
                checked={user.isSuperAdmin}
                onChange={(e) => handleSuperAdminToggle(e.target.checked)}
                disabled={updateUser.isPending}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:outline-none focus:ring-0"
              />
              <label htmlFor="super-admin-toggle" className="text-sm text-gray-700">
                {user.isSuperAdmin ? 'Super admin access granted' : 'Not a super admin'}
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Changing this requires confirmation. The checkbox saves immediately on confirm.
            </p>
          </div>
        </CardBody>
      </Card>

      {/* Team Info Card */}
      {user.teamId && (
        <Card padding="none">
          <CardHeader>
            <h2 className="text-lg font-semibold text-gray-900">Team Information</h2>
          </CardHeader>
          <CardBody>
            <div>
              <label className="text-sm font-medium text-gray-600">Team</label>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-gray-900">{user.teamName || 'Unknown'}</span>
                <Link
                  to={`/admin/teams/${user.teamId}`}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View Team
                </Link>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Agency Staff Memberships */}
      <Card padding="none">
        <CardHeader className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Portfolio Staff Memberships</h2>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowAddAgencyStaffModal(true)}
          >
            Add
          </Button>
        </CardHeader>
        <CardBody>
          {user.agencyStaff && user.agencyStaff.length > 0 ? (
            <div className="space-y-3">
              {user.agencyStaff.map((staff, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div>
                    <div className="font-medium text-gray-900">{staff.agencyName}</div>
                    <div className="text-sm text-gray-500">Role: {agencyRoleLabelEn(staff.role)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/admin/agencies/${staff.agencyId}`}
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                    >
                      View Brand Portfolio
                    </Link>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setRemoveAgencyConfirm({ open: true, agencyId: staff.agencyId, agencyName: staff.agencyName })}
                      disabled={removeAgencyStaff.isPending}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500 text-sm mb-3">No portfolio staff memberships</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddAgencyStaffModal(true)}
              >
                Add portfolio staff role
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Statistics Card */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Statistics</h2>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-600">Total Posts</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{user.postsCount}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Total Channels</label>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{user.channelsCount}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Last Login</label>
              <p className="mt-1 text-gray-900">
                {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Created</label>
              <p className="mt-1 text-gray-900">{formatDateTime(user.createdAt)}</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Impersonation History Card */}
      <Card padding="none">
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900">Impersonation History</h2>
        </CardHeader>
        <CardBody>
          {isLoadingHistory ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : historyError ? (
            <div className="text-red-600 text-sm">
              Error loading impersonation history: {historyError.message}
            </div>
          ) : !impersonationHistory?.data || impersonationHistory.data.length === 0 ? (
            <div className="text-gray-500 text-sm text-center py-8">
              No impersonation history
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admin
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Started At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ended At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mode
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {impersonationHistory.data.map((session) => (
                    <tr key={session.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {session.adminEmail}
                        </div>
                        {session.adminName && (
                          <div className="text-sm text-gray-500">{session.adminName}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {session.reason || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateTime(session.enteredAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {session.exitedAt ? (
                            formatDateTime(session.exitedAt)
                          ) : new Date(session.expiresAt) < new Date() ? (
                            <span className="text-orange-600 font-medium">Expired</span>
                          ) : (
                            <span className="text-green-600 font-medium">Active</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {session.exitedAt ? (
                            calculateDuration(session.enteredAt, session.exitedAt)
                          ) : new Date(session.expiresAt) < new Date() ? (
                            calculateDuration(session.enteredAt, session.expiresAt)
                          ) : (
                            'In Progress'
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {session.isReadOnly ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                            Read-Only
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                            Full Access
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog}
        onOpenChange={setDeleteDialog}
        title="Delete User"
        description={`Are you sure you want to delete ${user?.email}? This action cannot be undone.`}
        confirmText="Delete User"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        loading={deleteUser.isPending}
      />

      {/* Password Reset Confirmation Dialog */}
      <ConfirmDialog
        open={resetPasswordDialog}
        onOpenChange={setResetPasswordDialog}
        title="Reset Password"
        description={`Send password reset email to ${user?.email}?`}
        confirmText="Send Email"
        cancelText="Cancel"
        variant="default"
        onConfirm={handleResetPasswordConfirm}
        loading={resetPassword.isPending}
      />

      {/* Super Admin Confirmation Dialog */}
      <ConfirmDialog
        open={superAdminConfirmDialog}
        onOpenChange={(open) => {
          if (!open) {
            setSuperAdminConfirmDialog(false);
            setIsSuperAdminPending(null);
          }
        }}
        title={isSuperAdminPending ? 'Grant Super Admin Access' : 'Revoke Super Admin Access'}
        description={
          isSuperAdminPending
            ? `Are you sure? This grants ${user?.email} full system access including all admin controls.`
            : `Remove super admin access from ${user?.email}?`
        }
        confirmText={isSuperAdminPending ? 'Grant Access' : 'Revoke Access'}
        cancelText="Cancel"
        variant={isSuperAdminPending ? 'danger' : 'default'}
        onConfirm={handleSuperAdminSaveConfirm}
        loading={updateUser.isPending}
      />

      {/* Remove Agency Staff Confirmation Dialog */}
      <ConfirmDialog
        open={removeAgencyConfirm.open}
        onOpenChange={(open) => !open && setRemoveAgencyConfirm({ open: false, agencyId: null, agencyName: '' })}
        title="Remove Portfolio Staff"
        description={`Remove ${user?.email} from brand portfolio "${removeAgencyConfirm.agencyName}"?`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleRemoveAgencyStaffConfirm}
        loading={removeAgencyStaff.isPending}
      />

      {/* Edit Profile Modal */}
      {showEditProfileModal && user && (
        <EditProfileModal
          user={user}
          onClose={() => setShowEditProfileModal(false)}
          updateUser={updateUser}
        />
      )}

      {/* Add Agency Staff Modal */}
      {showAddAgencyStaffModal && user && (
        <AddAgencyStaffModal
          onClose={() => setShowAddAgencyStaffModal(false)}
          addAgencyStaff={addAgencyStaff}
          existingAgencyIds={(user.agencyStaff ?? []).map((s) => s.agencyId)}
        />
      )}

      {/* Password Reset Link Dialog */}
      <Dialog.Root open={!!resetLink} onOpenChange={(open) => { if (!open) setResetLink(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay
            className={cn(
              'fixed inset-0',
              'bg-black/50 backdrop-blur-sm',
              'z-modal-nested',
              'data-[state=open]:animate-fade-in',
              'data-[state=closed]:animate-fade-out',
              'duration-200'
            )}
          />
          <Dialog.Content
            className={cn(
              'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
              'w-[90vw] max-w-lg',
              'max-h-[90vh]',
              'bg-white rounded-2xl shadow-2xl',
              'overflow-hidden',
              'z-modal-nested',
              'focus:outline-none',
              'flex flex-col',
              'data-[state=open]:animate-fade-in',
              'data-[state=closed]:animate-fade-out'
            )}
          >
            {/* Header */}
            <div className="flex-shrink-0 px-6 pt-8 pb-6 bg-gradient-mint relative overflow-hidden">
              <div className="relative z-10 flex flex-col items-center">
                <div className={cn(
                  'w-16 h-16',
                  'bg-white/20 backdrop-blur-sm',
                  'rounded-full',
                  'flex items-center justify-center',
                  'mb-4',
                  'shadow-lg shadow-black/10',
                  'border border-white/20',
                  'animate-bounce-subtle'
                )}>
                  <Icon name={ICONS.LINK} size="lg" className="text-secondary-500 drop-shadow-sm" />
                </div>
                <Dialog.Title className="text-xl font-bold text-gray-900 text-center drop-shadow-sm">
                  Password Reset Link
                </Dialog.Title>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
              <Dialog.Description className="text-sm text-gray-600 text-center leading-relaxed">
                Email sent to <span className="font-medium text-gray-900">{user?.email}</span>.
                You can also share this link manually:
              </Dialog.Description>

              {/* Reset URL */}
              <div className="relative">
                <input
                  type="text"
                  readOnly
                  value={resetLink ?? ''}
                  aria-label="Password reset link"
                  className={cn(
                    'w-full pr-24 pl-3 py-2.5',
                    'text-xs font-mono text-gray-700',
                    'bg-gray-50 border border-gray-200 rounded-lg',
                    'focus:outline-none focus:border-primary-400',
                    'truncate'
                  )}
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCopyResetLink}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5"
                >
                  <Icon name={ICONS.COPY} size="sm" />
                  Copy
                </Button>
              </div>

              {/* Expiry note */}
              <p className="text-xs text-amber-600 flex items-center gap-1.5">
                <Icon name={ICONS.CLOCK} size="sm" />
                This link expires in 7 days.
              </p>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 pb-6 flex justify-end">
              <Button
                variant="secondary"
                onClick={() => setResetLink(null)}
              >
                Close
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

// ============================================================================
// EDIT PROFILE MODAL
// ============================================================================

const EDIT_TEAM_ROLES = ['OWNER', 'ADMIN', 'MEMBER', 'REVIEWER'] as const;

interface EditProfileModalProps {
  user: {
    id: number;
    fullName: string | null;
    timezone: string | null;
    isActive: boolean;
    teamId: number | null;
    teamName: string | null;
    teamRole: CreateUserRequest['teamRole'] | null;
    role: string;
  };
  onClose: () => void;
  updateUser: ReturnType<typeof useUpdateUser>;
}

const EditProfileModal = ({ user, onClose, updateUser }: EditProfileModalProps) => {
  const [formData, setFormData] = useState({
    fullName: user.fullName ?? '',
    timezone: user.timezone ?? '',
    isActive: user.isActive,
    teamRole: (user.teamRole ?? 'MEMBER') as CreateUserRequest['teamRole'],
  });

  // Two-state pattern: teamId = resolved form value, teamInputValue = Combobox display + search trigger
  const [teamId, setTeamId] = useState(user.teamId != null ? String(user.teamId) : '');
  const [teamInputValue, setTeamInputValue] = useState(user.teamName ?? '');
  const teamSearchQuery = useDebounce(teamInputValue, 300);
  const { data: teamsData, isLoading: isLoadingTeams } = useAdminTeams({
    search: teamSearchQuery || undefined,
    limit: 50,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const teamOptions = [
    { value: '', label: '(no team — unassign)' },
    ...(teamsData?.data ?? []).map((t) => ({ value: String(t.id), label: t.name })),
  ];

  const handleTeamComboboxChange = (val: string) => {
    if (val !== '' && !isNaN(parseInt(val, 10))) {
      setTeamId(val);
      const picked = teamOptions.find((t) => t.value === val);
      setTeamInputValue(picked?.label ?? val);
    } else {
      setTeamInputValue(val);
      setTeamId('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: Parameters<typeof updateUser.mutateAsync>[0]['data'] = {
      fullName: formData.fullName.trim() || null,
      timezone: formData.timezone.trim() || null,
      isActive: formData.isActive,
      teamId: teamId ? parseInt(teamId, 10) : null,
      teamRole: teamId ? formData.teamRole : undefined,
    };

    try {
      await updateUser.mutateAsync({ id: user.id, data: payload });
      toast.success('Profile updated');
      onClose();
    } catch (err) {
      if (isLastReviewerCancel(err)) return;
      toast.error('Failed to update profile');
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Edit Profile"
      size="sm"
      footer={
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={updateUser.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" form="edit-profile-form" loading={updateUser.isPending}>
            Save Changes
          </Button>
        </ModalFooter>
      }
    >
      <form id="edit-profile-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
          <Input
            type="text"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            placeholder="Jane Doe"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
          <Input
            type="text"
            value={formData.timezone}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            placeholder="Europe/Berlin"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="edit-is-active"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:outline-none focus:ring-0"
          />
          <label htmlFor="edit-is-active" className="text-sm font-medium text-gray-700">
            Account active
          </label>
        </div>

        {/* Team Assignment */}
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Team Assignment</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Team (leave empty to unassign)
            </label>
            <Combobox
              value={teamInputValue}
              onChange={handleTeamComboboxChange}
              options={teamOptions}
              placeholder="Search teams…"
              loading={isLoadingTeams}
              emptyMessage="No teams found"
            />
          </div>
          {teamId && (
            <div>
              <Select
                label="Team Role"
                value={formData.teamRole ?? 'MEMBER'}
                onValueChange={(v) => setFormData({ ...formData, teamRole: v as CreateUserRequest['teamRole'] })}
                options={EDIT_TEAM_ROLES.map((r) => ({ value: r, label: r }))}
              />
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

// ============================================================================
// ADD AGENCY STAFF MODAL
// ============================================================================

const ADD_AGENCY_ROLES = ['OWNER', 'ADMIN', 'MANAGER', 'MEMBER'] as const;

interface AddAgencyStaffModalProps {
  onClose: () => void;
  addAgencyStaff: ReturnType<typeof useAddAgencyStaff>;
  existingAgencyIds: number[];
}

const AddAgencyStaffModal = ({ onClose, addAgencyStaff, existingAgencyIds }: AddAgencyStaffModalProps) => {
  // Two-state pattern: agencyId = resolved form value, agencyInputValue = Combobox display + search trigger
  const [agencyId, setAgencyId] = useState('');
  const [agencyInputValue, setAgencyInputValue] = useState('');
  const [role, setRole] = useState<AgencyStaffRole>('MEMBER');
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Debounced server-search for agencies
  const agencySearchQuery = useDebounce(agencyInputValue, 300);
  const { data: agenciesData, isLoading: isLoadingAgencies } = useAdminAgencies({
    search: agencySearchQuery || undefined,
    limit: 50,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const availableAgencies = (agenciesData?.data ?? []).filter(
    (a) => !existingAgencyIds.includes(a.id)
  );
  const agencyOptions = availableAgencies.map((a) => ({
    value: String(a.id),
    label: a.name,
  }));

  const handleAgencyComboboxChange = (val: string) => {
    setInlineError(null);
    if (val !== '' && !isNaN(parseInt(val, 10))) {
      setAgencyId(val);
      const picked = agencyOptions.find((a) => a.value === val);
      setAgencyInputValue(picked?.label ?? val);
    } else {
      setAgencyInputValue(val);
      setAgencyId('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);

    if (!agencyId) {
      setInlineError('Please select a brand portfolio from the list.');
      return;
    }

    const parsedAgencyId = parseInt(agencyId, 10);
    if (isNaN(parsedAgencyId) || parsedAgencyId < 1) {
      setInlineError('Invalid brand portfolio selection.');
      return;
    }

    try {
      await addAgencyStaff.mutateAsync({ agencyId: parsedAgencyId, role });
      toast.success('Portfolio staff role added');
      onClose();
    } catch {
      toast.error('Failed to add portfolio staff role');
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Add Portfolio Staff Role"
      size="sm"
      footer={
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={addAgencyStaff.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-agency-staff-form"
            loading={addAgencyStaff.isPending}
            disabled={!agencyId || addAgencyStaff.isPending}
          >
            Add Role
          </Button>
        </ModalFooter>
      }
    >
      <form id="add-agency-staff-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Brand Portfolio *</label>
          <Combobox
            value={agencyInputValue}
            onChange={handleAgencyComboboxChange}
            options={agencyOptions}
            placeholder="Search brand portfolios…"
            loading={isLoadingAgencies}
            emptyMessage="No brand portfolios found (already staff of all matching)"
          />
          <p className="text-xs text-gray-500 mt-1">Brand portfolios already assigned are hidden.</p>
        </div>

        <div>
          <Select
            label="Role *"
            value={role}
            onValueChange={(v) => setRole(v as AgencyStaffRole)}
            options={ADD_AGENCY_ROLES.map((r) => ({ value: r, label: r }))}
          />
        </div>

        {inlineError && <p className="text-sm text-red-600">{inlineError}</p>}
      </form>
    </Modal>
  );
};

export default AdminUserDetail;
